import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Tooltip from '../Tooltip'
import styles from './Range.module.scss'

const THUMB_SIZE = 24

const Range = ({
  min = 0,
  max = 100,
  value: defaultValue = 0,
  step = 1,
  className = '',
  onChange = () => {},
  onInput = () => {},
  tooltip = true,
  disabled = false,
  tooltipTemplate = (value) => value,
}) => {
  const [value, setValue] = useState(min)
  const [changing, setChanging] = useState(false)
  const thumbRef = useRef(null)
  const [tooltipCSS, setTooltipCSS] = useState({})
  const [thumbCSS, setThumbCSS] = useState({})

  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  const handleRangeChange = useCallback((e) => {
    const value = +e.target.value
    e.target.style.setProperty('--val', value)
    setValue(value)

    const offset = (value * 100) / +max

    setThumbCSS({
      left: `calc(${offset}% - ${(offset * THUMB_SIZE) / 100}px)`,
    })
  }, [])

  const handleRangeInput = useCallback((e) => {
    const value = +e.target.value
    onInput(value)
  }, [])

  const handleRangeStart = useCallback(() => {
    setChanging(true)
  }, [])

  useEffect(() => {
    if (!thumbRef?.current) {
      return
    }

    const { x } = thumbRef.current.getBoundingClientRect()

    setTooltipCSS({
      left: x + THUMB_SIZE / 2 + 'px',
    })
  }, [thumbCSS, THUMB_SIZE, thumbRef.current])

  useEffect(() => {
    const handleRangeEnd = () => {
      if (changing) {
        setChanging(false)
        onChange(value)
      }
    }

    document.addEventListener('mouseup', handleRangeEnd)
    document.addEventListener('touchend', handleRangeEnd)

    return () => {
      document.removeEventListener('mouseup', handleRangeEnd)
      document.removeEventListener('touchend', handleRangeEnd)
    }
  }, [changing, onChange, value])

  return (
    <div className={clsx(styles['range'], className)}>
      <label className={styles['range__label']}>
        {tooltip && (
          <Tooltip
            title={tooltipTemplate(value)}
            shown={changing}
            style={tooltipCSS}
            tooltipClassName={styles['range__tooltip']}
          >
            <div
              ref={thumbRef}
              className={styles['range__thumb']}
              style={thumbCSS}
            />
          </Tooltip>
        )}
        <input
          type="range"
          min={+min}
          max={+max}
          step={+step}
          value={+value}
          className={styles['range__input']}
          onChange={handleRangeChange}
          onInput={handleRangeInput}
          onMouseDown={handleRangeStart}
          onTouchStart={handleRangeStart}
          disabled={disabled}
          style={{
            '--min': +min,
            '--max': +max,
            '--val': +value,
          }}
        />
      </label>
    </div>
  )
}

Range.propTypes = {
  min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  max: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  step: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  onChange: PropTypes.func,
  onInput: PropTypes.func,
  tooltip: PropTypes.bool,
  disabled: PropTypes.bool,
  tooltipTemplate: PropTypes.func,
}

export default Range
